/**
 * Types for the multi-PRO Bulk Document Search view (Search Results > Documents).
 * Mirrors backend/app/schemas/documents_search.py.
 */

/**
 * Which of V1's three document tables a row came from. The Documents view is a
 * UNION of all three — order documents, invoice PDFs, and driver-uploaded leg
 * documents (photos/signatures) — so a row's identity is the
 * (source, ref, fileinfo_recno) triple plus the PRO it was found under.
 * `map_recno` is NOT usable as a key: the three map tables have independent
 * recno sequences.
 */
export type DocumentSource = 'cms' | 'cmsinvoice' | 'legdocument';

export interface DocumentSearchRow {
  /** `{fileinfo_recno}_{ref}_{source}` — stable React key / grid row id. */
  doc_key: string;
  source: DocumentSource;
  /** pronumber (cms) / invoicehistoryrecno (cmsinvoice) / legdocumentinforecno. */
  ref: number;
  fileinfo_recno: number;
  pronumber: number;
  documenttype: string;
  description: string;
  /** GROUP_CONCAT of the PRO's invoice billtorefnos (may be empty). */
  billtorefno: string;
  filename: string;
  filetype: string;
  uploadedby: string;
  /** ISO datetime, or null when the source row has no upload timestamp. */
  uploaddatetime: string | null;
  deleted: 'Y' | 'N';
  /** GET /documents/preview, pre-built with this row's composite identity. */
  preview_url: string;
}

export interface DocumentSearchFilters {
  date_from?: string;
  date_to?: string;
  documenttype?: string[];
  revision?: 'active' | 'deleted' | 'all';
}

/**
 * Most documents one bulk action (email / zip / print) may carry.
 *
 * MUST match backend `MAX_BULK_FILES` in
 * backend/app/schemas/documents_search.py — every bulk endpoint declares
 * `items: List[BulkItem] = Field(..., max_length=MAX_BULK_FILES)`, so going
 * over it is a pydantic 422 with a raw validation message, not a friendly
 * error. The grid holds up to 10,000 rows and check-all can select all of
 * them, so the UI has to stop the user BEFORE the request.
 */
/**
 * Most PROs the Documents view may be asked about in one request.
 *
 * MUST match backend `MAX_SEARCH_PROS` in app/schemas/documents_search.py, where it is
 * `pros: List[int] = Field(..., max_length=MAX_SEARCH_PROS)` — going over is a 422
 * with a raw pydantic message, so the UI has to stop first.
 *
 * This is the reason Search Results gates its Documents toggle: the Custom Filter is
 * deliberately uncapped (it backs large exports), so a result set can legitimately be
 * bigger than any one documents request.
 */
export const MAX_SEARCH_PROS = 10000;

export const MAX_BULK_SELECTION = 500;

/**
 * Most documents ONE print (PDF merge) may carry — stricter than
 * MAX_BULK_SELECTION because the merge holds every page in memory server-side.
 *
 * MUST match backend `MAX_PRINT_DOCS` in
 * backend/app/api/api_v1/endpoints/documents_bulk.py, which 413s over it. Print
 * claims a browser tab on click and only fails seconds later, so an unpredicted
 * cap costs the user a placeholder tab plus the whole merge wait — every other
 * cap in this view (500 items, 20MB email) is predicted client-side, and this
 * one has to be too.
 */
export const MAX_PRINT_SELECTION = 100;

/**
 * One selected document, in the shape every bulk endpoint authorizes on.
 *
 * `pronumber` is not redundant with `ref`: for cmsinvoice/legdocument the ref
 * points at an invoice batch / leg record that can span several PROs. The
 * server re-resolves the PRO and 400s the whole request unless it matches this
 * claim, so an invented ref cannot reach another PRO's document.
 */
export interface BulkItem {
  source: DocumentSource;
  ref: number;
  fileinfo_recno: number;
  pronumber: number;
}

export interface BulkSizeItem {
  doc_key: string;
  /** doc_key can repeat across PROs (one invoice batch, several PROs). */
  pronumber: number;
  /** -1 when the blob HEAD failed (size unknown, not zero). */
  size_bytes: number;
}

export interface BulkSizeResponse {
  total_bytes: number;
  /** The server's real email cap (MAX_SENDGRID_SIZE), so the UI shows truth. */
  limit_bytes: number;
  items: BulkSizeItem[];
}

export interface BulkFailure {
  pronumber: number;
  filename: string;
  reason: string;
}

export interface BulkEmailResponse {
  sent: number;
  failed: BulkFailure[];
  recipients: string[];
}

export interface DocumentSearchResponse {
  rows: DocumentSearchRow[];
  /** True when the server row cap was hit — the UI must say so (no silent caps). */
  truncated: boolean;
}
